import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, PieChart, TrendingUp, DollarSign, Calendar,
  FileText, Download, BarChart3, Target, Lightbulb, 
  CheckCircle, Filter
} from 'lucide-react';

const ReportsGuide = () => {
  const navigate = useNavigate();

  const reports = [
    {
      id: 'profit-loss',
      title: 'Profit & Loss Report',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
      description: 'Your business\'s financial health at a glance',
      details: [
        'Gross revenue from all paid invoices',
        'Expenses broken down by category',
        'Net profit (revenue minus expenses)',
        'Tax set-aside recommendation',
        'Month-over-month and year-over-year comparisons'
      ],
      bestFor: 'Understanding overall business performance and profitability'
    },
    {
      id: 'schedule-c',
      title: 'Schedule C Report',
      icon: FileText,
      color: 'bg-blue-100 text-blue-600',
      description: 'Tax-ready breakdown organized by IRS line numbers',
      details: [
        'Income and expenses mapped to Schedule C lines',
        'Gross receipts (Line 1)',
        'Cost of goods sold breakdown',
        'Expense categories (Lines 8-27)',
        'Estimated self-employment tax'
      ],
      bestFor: 'Tax preparation - hand this to your accountant or use for DIY filing'
    },
    {
      id: 'quarterly-tax',
      title: 'Quarterly Tax Summary',
      icon: Calendar,
      color: 'bg-purple-100 text-purple-600',
      description: 'Estimated quarterly tax payments',
      details: [
        'Income and expenses by quarter',
        'Self-employment tax (15.3%)',
        'Federal income tax estimate',
        'State income tax (based on your settings)',
        'Payment due dates and amounts'
      ],
      bestFor: 'Calculating quarterly estimated tax payments to avoid penalties'
    },
    {
      id: 'revenue',
      title: 'Revenue Analytics',
      icon: BarChart3,
      color: 'bg-orange-100 text-orange-600',
      description: 'Track revenue trends and growth',
      details: [
        'Monthly and weekly revenue charts',
        'Revenue by service type',
        'Average job value',
        'Customer acquisition trends',
        'Seasonal patterns'
      ],
      bestFor: 'Identifying growth opportunities and seasonal trends'
    }
  ];

  const features = [
    {
      icon: Filter,
      title: 'Date Range Filtering',
      description: 'View reports for any time period - this month, last quarter, year-to-date, or custom dates'
    },
    {
      icon: Download,
      title: 'PDF Export',
      description: 'Export any report to PDF for your records, accountant, or loan applications'
    },
    {
      icon: Target,
      title: 'Real-Time Data',
      description: 'Reports update automatically as you create invoices and log expenses'
    }
  ];

  const faqs = [
    {
      question: "Why don't my numbers match my bank account?",
      answer: "SuddyFlow reports are based on invoices marked as PAID and expenses you've logged. Make sure all payments are recorded and expenses are entered. Also check that you're viewing the correct date range."
    },
    {
      question: "What's the difference between P&L and Schedule C reports?",
      answer: "The P&L gives you a business overview with categories that make sense for day-to-day management. Schedule C organizes the same data by IRS line numbers for tax filing. Use P&L for business decisions, Schedule C for taxes."
    },
    {
      question: "How accurate are the tax estimates?",
      answer: "They're rough estimates based on standard tax rates. Your actual taxes depend on filing status, other income, deductions, and credits. Use these as a starting point and consult a tax professional for accuracy."
    },
    {
      question: "Can I see reports for specific customers or services?",
      answer: "Currently, reports show business-wide totals. Customer-specific and service-specific reports are on our roadmap. You can filter the Jobs and Invoices lists to see data for specific customers."
    },
    {
      question: "What expenses should I be tracking?",
      answer: "Track everything business-related: fuel, supplies, chemicals, equipment, insurance, phone, software subscriptions, vehicle maintenance, uniforms, marketing, and professional services. The more you track, the lower your taxable income!"
    }
  ];

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Reports & Analytics</h1>
          <p className="text-lg text-gray-600">
            Understand your business performance with powerful reports and insights.
          </p>
        </div>

        {/* Quick Access */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-4 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PieChart className="text-primary-600" size={24} />
            <span className="font-medium text-gray-900">Ready to see your numbers?</span>
          </div>
          <button
            onClick={() => navigate('/reports')}
            className="btn btn-primary text-sm"
          >
            Go to Reports
          </button>
        </div>

        {/* Available Reports */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Available Reports</h2>
          <div className="space-y-4">
            {reports.map((report) => {
              const Icon = report.icon;
              return (
                <div 
                  key={report.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${report.color}`}>
                        <Icon size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">{report.title}</h3>
                        <p className="text-gray-600 text-sm mb-3">{report.description}</p>
                        
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">What's included:</p>
                          <ul className="space-y-1">
                            {report.details.map((detail, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Lightbulb size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Best for:</span> {report.bestFor}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                    <Icon size={20} className="text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How to Use */}
        <div className="mb-10 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">How to Use Reports</h2>
          
          <ol className="space-y-4">
            <li className="flex items-start gap-4">
              <span className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <div>
                <h3 className="font-medium text-gray-900">Navigate to Reports</h3>
                <p className="text-sm text-gray-600">Click "Reports" in the main navigation menu</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <div>
                <h3 className="font-medium text-gray-900">Select a Report</h3>
                <p className="text-sm text-gray-600">Choose from P&L, Schedule C, Quarterly Tax, or Revenue Analytics</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <div>
                <h3 className="font-medium text-gray-900">Set Date Range</h3>
                <p className="text-sm text-gray-600">Use the date picker to view specific periods (month, quarter, year, or custom)</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
              <div>
                <h3 className="font-medium text-gray-900">Export if Needed</h3>
                <p className="text-sm text-gray-600">Click "Export PDF" to download a copy for your records or accountant</p>
              </div>
            </li>
          </ol>
        </div>

        {/* Pro Tips */}
        <div className="mb-10 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
            <Lightbulb size={24} />
            Pro Tips
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Review monthly:</strong> Check your P&L at the end of each month to catch issues early and track progress toward goals.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Log expenses regularly:</strong> Don't wait until tax time! Log expenses weekly to ensure accurate reports and maximize deductions.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Compare periods:</strong> Look at this month vs. last month, or this quarter vs. same quarter last year to spot trends.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Save for taxes:</strong> Use the Quarterly Tax report to set aside the right amount each quarter - avoid surprises in April!
              </p>
            </li>
          </ul>
        </div>

        {/* FAQs */}
        <div className="mb-10 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className={index !== faqs.length - 1 ? 'pb-4 border-b border-gray-100' : ''}>
                <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Topics */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Related Topics</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/help/tax-settings')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
            >
              Tax Settings & Preparation
            </button>
            <button
              onClick={() => navigate('/help/invoices')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
            >
              Creating Invoices
            </button>
            <button
              onClick={() => navigate('/help/best-practices')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
            >
              Best Practices
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-8 text-center border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Ready to See Your Numbers?
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Check out your reports to understand how your business is performing!
          </p>
          <button
            onClick={() => navigate('/reports')}
            className="btn btn-primary"
          >
            View Reports
          </button>
        </div>

        {/* Back to Help Center */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsGuide;